<template>
  <div class="jobs-configuration" data-testid="jobs-configuration">
    <!-- Breadcrumb: back to the Configuration panel grid. -->
    <ExBreadcrumbs class="jobs-configuration__breadcrumbs" :breadcrumbsList="breadcrumbItems" />

    <div class="jobs-configuration__section">
      <!-- Section header. -->
      <div class="jobs-configuration__header">
        <h2 class="jobs-configuration__title">Job Categories and Families</h2>
        <p class="jobs-configuration__subtitle">
          This configuration controls visibility of Job Categories and Families in this account
        </p>
      </div>

      <hr class="jobs-configuration__divider" />

      <p
        v-if="errorMessage"
        class="jobs-configuration__error"
        role="alert"
        data-testid="jobs-error"
      >
        {{ errorMessage }}
      </p>

      <!-- Job Categories row. -->
      <div class="jobs-configuration__row">
        <div class="jobs-configuration__label-block">
          <span class="jobs-configuration__name">Job Categories</span>
          <span class="jobs-configuration__description">
            Group Positions by equal-value work, scored across skills, effort, responsibility, and
            working conditions, so pay can be compared fairly within a category and reported for EU
            Pay Transparency compliance.
          </span>
        </div>
        <div class="jobs-configuration__toggle-block">
          <SwitchButton
            v-model="jobCategoriesOn"
            :label="'Allow Job Categories'"
            :toggleLoader="savingCategories"
            :disabled="loading"
            data-testid="jobs-categories-toggle"
            @onChange="handleCategoriesChange"
          />
          <span class="jobs-configuration__supporting-text">
            Visible in Positions and Organization > Job Categories
          </span>
        </div>
      </div>

      <hr class="jobs-configuration__divider" />

      <!-- Job Families row. -->
      <div class="jobs-configuration__row">
        <div class="jobs-configuration__label-block">
          <span class="jobs-configuration__name">Job Families</span>
          <span class="jobs-configuration__description">
            Group Positions into disciplines — Engineering, Sales, Finance, and so on — each with
            its own career ladder of Levels (e.g. Engineer I → Senior → Staff → Principal).
          </span>
        </div>
        <div class="jobs-configuration__toggle-block">
          <SwitchButton
            v-model="jobFamiliesOn"
            :label="'Allow Job Families'"
            :toggleLoader="savingFamilies"
            :disabled="isFamiliesToggleDisabled"
            data-testid="jobs-families-toggle"
            @onChange="handleFamiliesChange"
          />
          <span class="jobs-configuration__supporting-text">
            Visible in Positions and Organization > Job Families
          </span>
          <span
            v-if="isFamiliesLocked"
            class="jobs-configuration__supporting-text jobs-configuration__supporting-text--locked"
            data-testid="jobs-families-locked-text"
          >
            Locked to allow because account uses product Fair Pay
          </span>
        </div>
      </div>

      <hr class="jobs-configuration__divider" />

      <!-- Job Category Scoring (weights + value bands) section. -->
      <div class="jobs-configuration__scoring">
        <JobCategoryScoring :account-id="resolvedAccountId" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from "vue";
import SwitchButton from "@components/SwitchButton/SwitchButton.vue";
import JobCategoryScoring from "./JobCategoryScoring.vue";
import ExBreadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import { getAccountData, updateDirectory } from "@/api";
import { LD_FLAG_IDEA_MDP_581 } from "@components/constants/launchDarklyFlagKeys";

const props = defineProps({
  /**
   * Account id to load job configuration for. Falls back to the
   * `currentAccountID` localStorage value used by the rest of the Settings flow.
   */
  accountId: {
    type: [Number, String],
    default: null,
  },
});

const breadcrumbItems = [
  { text: "Configuration", href: "/settings/configuration", disabled: false },
  { text: "Jobs", disabled: true },
];

const { proxy } = getCurrentInstance() || {};

const jobCategoriesOn = ref(false);
const jobFamiliesOn = ref(false);
const loading = ref(false);
const savingCategories = ref(false);
const savingFamilies = ref(false);
const errorMessage = ref("");

// Fair Pay product flag read straight off the account response this page already
// fetches (not the localStorage helper, which can go stale until a manual refresh).
const isFairPayEnabled = ref(false);
// idea-MDP-581 kill switch gating the Fair Pay lock behaviour (see R6).
const fairPayFeatureEnabled = ref(true);

const isFamiliesLocked = computed(() => isFairPayEnabled.value && fairPayFeatureEnabled.value);
const isFamiliesToggleDisabled = computed(() => loading.value || isFamiliesLocked.value);

const resolvedAccountId = computed(() => {
  if (props.accountId !== null && props.accountId !== undefined && props.accountId !== "") {
    return props.accountId;
  }
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return null;
  }
});

async function resolveFairPayFeatureFlag() {
  try {
    fairPayFeatureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_581, true)) !== false;
  } catch {
    // Kill-switch lookup failures never lock the toggle shut.
    fairPayFeatureEnabled.value = true;
  }
}

async function fetchJobConfiguration() {
  const accountId = resolvedAccountId.value;
  if (accountId === null || accountId === undefined) {
    errorMessage.value = "Could not determine the account to load job configuration for.";
    return;
  }

  loading.value = true;
  errorMessage.value = "";
  try {
    const { data } = await getAccountData(accountId);
    jobCategoriesOn.value = data?.job_categories_on === true;
    isFairPayEnabled.value = data?.product_fair_pay === true;
    jobFamiliesOn.value = data?.job_families_on === true;

    // Fair Pay forces Job Families on (locked) and must actually persist that on
    // value, so visibility elsewhere (Positions / Organization > Job Families)
    // matches what is shown here rather than being a cosmetic override.
    if (isFamiliesLocked.value) {
      jobFamiliesOn.value = true;
      if (data?.job_families_on !== true) {
        await persistToggle("job_families_on", true);
      }
    }
  } catch {
    jobCategoriesOn.value = false;
    jobFamiliesOn.value = false;
    errorMessage.value = "Failed to load job configuration. Please try again.";
  } finally {
    loading.value = false;
  }
}

async function persistToggle(field, value) {
  const accountId = resolvedAccountId.value;
  if (accountId === null || accountId === undefined) {
    errorMessage.value = "Could not determine the account to update.";
    return false;
  }

  errorMessage.value = "";
  try {
    await updateDirectory(accountId, { [field]: value });
    return true;
  } catch {
    errorMessage.value = "Failed to save this setting. Please try again.";
    return false;
  }
}

async function handleCategoriesChange({ value }) {
  if (savingCategories.value) return;
  savingCategories.value = true;
  try {
    const saved = await persistToggle("job_categories_on", value);
    if (!saved) {
      jobCategoriesOn.value = !value;
    }
  } finally {
    savingCategories.value = false;
  }
}

async function handleFamiliesChange({ value }) {
  if (isFamiliesLocked.value) return;
  if (savingFamilies.value) return;
  savingFamilies.value = true;
  try {
    const saved = await persistToggle("job_families_on", value);
    if (!saved) {
      jobFamiliesOn.value = !value;
    }
  } finally {
    savingFamilies.value = false;
  }
}

onMounted(async () => {
  await resolveFairPayFeatureFlag();
  await fetchJobConfiguration();
});
</script>

<style scoped>
.jobs-configuration {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.jobs-configuration__breadcrumbs {
  width: 100%;
}

.jobs-configuration__section {
  width: 100%;
  max-width: 1032px;
  padding: 32px;
}

.jobs-configuration__header {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 20px;
}

.jobs-configuration__title {
  margin: 0;
  font-family: Inter;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
}

.jobs-configuration__subtitle {
  margin: 0;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.jobs-configuration__error {
  margin: 16px 0;
  padding: 12px 16px;
  border: 1px solid #fecaca;
  border-radius: 8px;
  background: #fef2f2;
  color: #b42318;
  font-size: 14px;
  line-height: 20px;
}

.jobs-configuration__row {
  display: flex;
  align-items: flex-start;
  gap: 64px;
  padding: 16px 0;
}

.jobs-configuration__label-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 0 0 280px;
  max-width: 280px;
}

.jobs-configuration__name {
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
}

.jobs-configuration__description {
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.jobs-configuration__toggle-block {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
}

.jobs-configuration__supporting-text {
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #667085;
}

.jobs-configuration__divider {
  border: 0;
  border-top: 1px solid #eaecf0;
  margin: 0;
  width: 100%;
}

.jobs-configuration__scoring {
  margin-top: 24px;
}
</style>
